import {
  MapImageProvider,
  MapStorageProvider,
  ProjectDatabaseProvider
} from '@/modules/mapSnapshotService/types';
import { BackendClient } from '@/infrastructure/clients/BackendClient';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { projectPatchBody } from '@/infrastructure/api/v1/mappers/projects';
import { fetchProjectRow } from '@/modules/databaseService/projects';
import { objectPathOf } from '@/utils/storageAddress';

export class MapSnapshotAdapter implements MapImageProvider, MapStorageProvider, ProjectDatabaseProvider {
  async fetchMapImage(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch map image');
    return response.blob();
  }
  async storeMapImage(params: {
    image: Blob;
    projectInstanceId: string;
    metadata: { contentType: string };
  }): Promise<{ url: string; error?: Error }> {
    try {
      const version = Date.now();
      const path = `project_maps/${params.projectInstanceId}/${version}.png`;
      
      const { data: stored, error: uploadError } = await BackendClient.storage
        .from('image_files')
        .upload(path, params.image, {
          contentType: params.metadata.contentType,
          upsert: true,
          cacheControl: '0'
        });

      if (uploadError) return { url: '', error: new Error(uploadError.message) };

      // The address the upload answered: the stored name has a version in it
      return { url: stored?.url || '' };
    } catch (error) {
      return { url: '', error: error as Error };
    }
  }

  async getProjectMapImageUrl(projectInstanceId: string): Promise<{ url: string | null; error?: Error }> {
    try {
      const { data, error } = await fetchProjectRow(projectInstanceId);

      if (error) return { url: null, error };
      return { url: data?.project_instance_map_img_url || null };
    } catch (error) {
      return { url: null, error: error as Error };
    }
  }

  async updateProjectMapImageUrl(params: {
    projectInstanceId: string;
    imageUrl: string;
    userId: string;
  }): Promise<{ error?: Error }> {
    try {
      const { error } = await toResult(v1.PATCH('/api/v1/projects/{projectUniqueId}', {
        params: { path: { projectUniqueId: params.projectInstanceId } },
        body: projectPatchBody({ project_instance_map_img_url: params.imageUrl }),
      }));

      return { error: error || undefined };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async deleteMapImage(url: string): Promise<{ error?: Error }> {
    try {
      // The path from the public URL, in any form a stored address has taken (the CDN's, Supabase's or the backend's)
      const path = objectPathOf(url, 'image_files');
      if (!path) return { error: new Error('Invalid storage URL') };

      const { error } = await BackendClient.storage.from('image_files').remove([path]);

      return { error: error ? new Error(error.message) : undefined };
    } catch (error) {
      return { error: error as Error };
    }
  }
}
